import { formatCents, formatCount, tt } from '@agos/shared'

import { smsRunCostCents } from './campaigns'

/**
 * *Poslať teraz* - `FRONTEND.md` §9.778, BACKEND.md §10A.433, owner O138d.
 *
 * Who a campaign sent now is for, how many that is, and how far the send has got. The server decides what a
 * send does for each campaign (`CampaignTexts.sendKind`); this mirrors it only to say so on the card, and
 * `CampaignSendKindsAgreeTest` holds the two together.
 */

export type SendKind = 'announcement' | 'offer' | 'own-run' | 'coupon' | 'points'

const ANNOUNCERS: readonly string[] = [
  'happy_hours',
  'black_friday',
  'free_delivery_weekend',
  'push_blast',
  'click_and_win',
  'scratch_card',
]

/** §9.780, BACKEND.md §10A.434 - the order rewards sent by hand as a coupon. `CampaignTexts.REWARD_COUPONS`. */
const REWARD_COUPONS: readonly string[] = ['kupon_za_vernost', 'kuponove_tornado', 'cashback']

/** What sending this campaign now does, or null when it cannot be sent now. */
export function sendKindOf(type: string): SendKind | null {
  if (ANNOUNCERS.includes(type)) return 'announcement'
  if (REWARD_COUPONS.includes(type)) return 'coupon'
  if (type === 'bonusove_body') return 'points'
  if (type === 'personalizovana_ponuka') return 'offer'
  if (type === 'meninova_zlava' || type === 'reaktivacia_neaktivnych') return 'own-run'
  return null
}

/** The sentence under *Poslať teraz*: what a send of this campaign is. */
export function sendKindNote(kind: SendKind, type: string): string {
  if (kind === 'coupon') return couponNote(type)
  if (kind === 'points') {
    // §9.781, BACKEND.md §10A.435 - the points are added even when the message does not reach the customer,
    // and the day's skip is what stops a second press adding them twice.
    return tt(
      'Každý zo zvolených zákazníkov dostane body z karty a správu o nich. Komu body pribudli v posledný deň, tomu sa nepridajú znova.',
    )
  }
  if (kind === 'announcement') {
    return tt('Pošle oznámenie bez kupónu - zľavu uplatní košík, hru zákazník nájde vo svojom profile.')
  }
  if (kind === 'offer') {
    return tt(
      'Každý dostane ponuku na jedlo, ktoré si objednáva najčastejšie, s kupónom na tento týždeň. Kto také jedlo nemá, preskočí sa.',
    )
  }
  return tt('Pošle sa len tým zo zvolených zákazníkov, pre ktorých je kampaň určená.')
}

/**
 * §9.780 - what an order reward sent by hand is: the campaign's own coupon to each, in the texts under
 * *Pri ručnom poslaní*. The loyalty coupon goes to members alone, as the automatic one does; cashback has no
 * order to take a share of, so it is the percentage off the next one.
 */
function couponNote(type: string): string {
  const each = tt('Každý dostane vlastný kupón podľa nastavení kampane, s textami Pri ručnom poslaní.')
  if (type === 'kupon_za_vernost') {
    return `${each} ${tt('Posiela sa len členom vernostného programu.')}`
  }
  if (type === 'cashback') {
    return `${each} ${tt('Bez objednávky sa nemá z čoho vrátiť podiel, preto je cashback percentuálna zľava z ďalšej objednávky.')}`
  }
  return each
}

export type AudienceKind = 'all' | 'loyalty' | 'segment' | 'ordered' | 'notOrdered'

export const AUDIENCE_KINDS: readonly AudienceKind[] = ['all', 'loyalty', 'segment', 'ordered', 'notOrdered']

export interface AudienceForm {
  kind: AudienceKind
  segment: string
  days: string
}

export const EMPTY_AUDIENCE: AudienceForm = { kind: 'all', segment: 'champions', days: '30' }

export function audienceLabel(kind: AudienceKind): string {
  if (kind === 'loyalty') return tt('Členom vernostného programu')
  if (kind === 'segment') return tt('Segmentu zákazníkov')
  if (kind === 'ordered') return tt('Objednali za posledných N dní')
  if (kind === 'notOrdered') return tt('Neobjednali aspoň N dní')
  return tt('Všetkým zákazníkom')
}

function needsDays(kind: AudienceKind): boolean {
  return kind === 'ordered' || kind === 'notOrdered'
}

/** What is wrong with the audience, so the preview is not asked for nonsense. */
export function audienceProblems(form: AudienceForm): string[] {
  if (!needsDays(form.kind)) return []
  const days = Number(form.days)
  return form.days.trim() === '' || !Number.isInteger(days) || days < 1
    ? [tt('Počet dní musí byť celé číslo aspoň 1.')]
    : []
}

/** The audience as `POST /campaigns/id/{id}/send` takes it. */
export function audiencePayload(form: AudienceForm): {
  loyaltyOnly?: boolean
  segment?: string
  orderedWithinDays?: number
  notOrderedForDays?: number
} {
  if (form.kind === 'loyalty') return { loyaltyOnly: true }
  if (form.kind === 'segment') return { segment: form.segment }
  if (form.kind === 'ordered') return { orderedWithinDays: Number(form.days) }
  if (form.kind === 'notOrdered') return { notOrderedForDays: Number(form.days) }
  return {}
}

export interface SendPreview {
  recipients: number
  push: number
  email: number
  sms: number
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

export function sendPreviewOf(payload: unknown): SendPreview | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.recipients !== 'number') return null
  return { recipients: count(row.recipients), push: count(row.push), email: count(row.email), sms: count(row.sms) }
}

/**
 * How many the send reaches, said before the button: the audience and what each ticked channel can carry.
 * With SMS ticked, what it would cost at most - §9.339's rule that a count with no price does not say it costs.
 */
export function previewSentence(preview: SendPreview, channels: readonly string[]): string {
  const parts = [`${tt('Zákazníkov')}: ${formatCount(preview.recipients)}`]
  if (channels.includes('push')) parts.push(`${tt('s push notifikáciami')}: ${formatCount(preview.push)}`)
  if (channels.includes('email')) parts.push(`${tt('s e-mailom')}: ${formatCount(preview.email)}`)
  if (channels.includes('sms')) {
    parts.push(
      `${tt('so súhlasom na SMS')}: ${formatCount(preview.sms)} (${tt('najviac')} ${formatCents(smsRunCostCents(preview.sms))})`,
    )
  }
  return parts.join(' · ')
}

export type SendStatus = 'running' | 'done' | 'refused' | 'error'

export interface SendJob {
  jobId: string
  status: SendStatus
  total: number
  processed: number
  reached: number
  failed: number
  skipped: number
  message: string | null
}

export function sendJobOf(payload: unknown): SendJob | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.jobId !== 'string') return null
  const status: SendStatus =
    row.status === 'done' || row.status === 'refused' || row.status === 'error' ? row.status : 'running'
  return {
    jobId: row.jobId,
    status,
    total: count(row.total),
    processed: count(row.processed),
    reached: count(row.reached),
    failed: count(row.failed),
    skipped: count(row.skipped),
    message: typeof row.message === 'string' && row.message !== '' ? row.message : null,
  }
}

export function jobRunning(job: SendJob | null): boolean {
  return job !== null && job.status === 'running'
}

/** How far a send got, in the run history's words. */
export function jobSentence(job: SendJob): string {
  if (job.status === 'refused') return `${tt('Neodoslané')}: ${job.message ?? ''}`.trim()
  if (job.status === 'error') return `${tt('Odosielanie sa prerušilo')}: ${job.message ?? ''}`.trim()
  const counts = `${tt('zastihnutých')} ${formatCount(job.reached)}, ${tt('preskočených')} ${formatCount(job.skipped)}, ${tt('neúspešných')} ${formatCount(job.failed)}`
  if (job.status === 'running') {
    return `${tt('Odosiela sa')}: ${formatCount(job.processed)} / ${formatCount(job.total)} - ${counts}`
  }
  return `${tt('Odoslané')}: ${counts}`
}
